/**
 * Analytics Results API client.
 *
 * Saves and retrieves analytics results for a community.
 * Results are stored per-community and per result_type:
 *   'forecast'     - Short-term or long-term forecast results
 *   'optimization' - Day-ahead MILP optimization results
 *   'cba'          - Cost-benefit analysis results
 *   'kpis'         - Long-term KPI trajectory results
 *
 * All requests route through /api/communities/:id/results,
 * which is proxied to scenario-service:8002 via Vite / nginx.
 */

import client from './client';

export const resultsAPI = {
  /**
   * Save an analytics result for a community.
   * @param {string} communityId - Community UUID
   * @param {{ result_type, name?, parameters?, result_data }} data
   * @returns {Promise<{ id, name, result_type, created_at, status }>}
   */
  save: (communityId, data) =>
    client.post(`/api/communities/${communityId}/results`, data),

  /**
   * List saved results for a community (most recent first, max 50).
   * @param {string} communityId - Community UUID
   * @returns {Promise<Array<{ id, result_type, name, created_at, parameters }>>}
   */
  list: (communityId) =>
    client.get(`/api/communities/${communityId}/results`),

  /**
   * Get a single saved result including full result_data.
   * @param {string} communityId - Community UUID
   * @param {string} resultId - Result UUID
   * @returns {Promise<{ id, result_type, name, created_at, parameters, result_data }>}
   */
  get: (communityId, resultId) =>
    client.get(`/api/communities/${communityId}/results/${resultId}`),

  /**
   * Delete a saved result.
   * @param {string} communityId - Community UUID
   * @param {string} resultId - Result UUID
   */
  delete: (communityId, resultId) =>
    client.delete(`/api/communities/${communityId}/results/${resultId}`),
};
